import {
    IMappingProfile,
    IQueryParams,
    IQueryParamsSymbol,
    PFLoadEntities,
} from '@pf/shared-common';
import {
    entitySymbols,
    withDefaultEntityMetadata,
    withDefaultSearchParamsMappingConfiguration,
    withDefaultSearchParamsMetadata,
    withMetadata,
    zipToTimezone,
} from '@pf/shared-utility';
import {
    StopCreate,
    StopDto,
    StopRead,
    StopSearchParams,
} from '../../entities/stop.dto';
import {
    ContactMetadataMap,
    CustomFieldMetadataMap,
    DefaultCustomerFragmentMetadataMap,
    DefaultLocationFragmentMetadataMap,
    DefaultVendorFragmentMetadataMap,
    ITypeEntityFragmentSymbol,
    ResourceAuthSymbol,
} from '@pf/shared/util-platform';
import { EventMetadataMap } from '../../entities/event.dto';
import { ReferenceMetadataMap } from '../../entities/reference.dto';
import { createMap, forMember, mapFrom, Mapper } from '@automapper/core';
import { StopChargeMetadataMap } from '../../entities/stop-charge.dto';
import { StopItemMetadataMap } from '../stop-items/stop-item.mapping-profile';
import { StopUnifiedFragmentRead } from '../../entities/stop.unified.dto';
import { NoteMetadataMap } from '../../entities/notes.dto';
import { StopStatusMetadataMap } from '../../entities/stop-status.dto';

const symbols = entitySymbols(PFLoadEntities.Stop);

withDefaultEntityMetadata<StopDto>(symbols.dto, {
    specialInstructions: String,
    expectedArrival: String,
    expectedDeparture: String,
    arrival: String,
    departure: String,
    location: DefaultLocationFragmentMetadataMap.symbols.dto,
    customer: DefaultCustomerFragmentMetadataMap.symbols.dto,
    vendor: DefaultVendorFragmentMetadataMap.symbols.dto,
    stopType: ITypeEntityFragmentSymbol,
    stopStatus: StopStatusMetadataMap.symbols.dto,
    charges: [StopChargeMetadataMap.symbols.dto],
    events: [EventMetadataMap.symbols.dto],
    contacts: [ContactMetadataMap.symbols.dto],
    references: [ReferenceMetadataMap.symbols.dto],
    customFields: [CustomFieldMetadataMap.symbols.dto],
    stopItems: [StopItemMetadataMap.symbols.dto],
    resourceAuth: ResourceAuthSymbol,
    timezone: String,
});

withDefaultEntityMetadata<StopRead & StopUnifiedFragmentRead>(symbols.entity, {
    specialInstructions: String,
    expectedArrival: String,
    expectedDeparture: String,
    arrival: String,
    departure: String,
    location: DefaultLocationFragmentMetadataMap.symbols.entity,
    customer: DefaultCustomerFragmentMetadataMap.symbols.entity,
    vendor: DefaultVendorFragmentMetadataMap.symbols.entity,
    stopType: ITypeEntityFragmentSymbol,
    stopStatus: StopStatusMetadataMap.symbols.entity,
    charges: [StopChargeMetadataMap.symbols.entity],
    events: [EventMetadataMap.symbols.entity],
    notes: [NoteMetadataMap.symbols.entity],
    references: [ReferenceMetadataMap.symbols.entity],
    resourceAuth: ResourceAuthSymbol,
    customFields: [CustomFieldMetadataMap.symbols.entity],
    sequence: Number,
    stopItems: [StopItemMetadataMap.symbols.entity],
});

withMetadata<StopCreate>(symbols.createBody, {
    specialInstructions: String,
    expectedArrival: String,
    expectedDeparture: String,
    arrival: String,
    departure: String,
    locationId: String,
    customerId: String,
    vendorId: String,
    stopTypeId: String,
    stopItems: [StopItemMetadataMap.symbols.createBody],
    stopStatus: StopStatusMetadataMap.symbols.createBody,
    charges: [StopChargeMetadataMap.symbols.createBody],
    events: [EventMetadataMap.symbols.createBody],
    contacts: [ContactMetadataMap.symbols.createBody],
    references: [ReferenceMetadataMap.symbols.createBody],
    notes: [String],
    resourceAuth: ResourceAuthSymbol,
});

withDefaultSearchParamsMetadata<StopSearchParams>(symbols.searchParams, {});

export const StopMetadataMap: IMappingProfile = {
    symbols,
    profile: (mapper: Mapper) => {
        createMap<StopRead, StopDto>(
            mapper,
            symbols.entity,
            symbols.dto,
            forMember(
                d => d.timezone,
                mapFrom(source =>
                    zipToTimezone(source.location?.address?.postalCode)
                )
            )
        );
        createMap<StopDto, StopRead>(mapper, symbols.dto, symbols.entity);
        createMap<StopDto, StopCreate>(
            mapper,
            symbols.dto,
            symbols.createBody,
            forMember(
                e => e.stopTypeId,
                mapFrom(s => s.stopType?.id)
            ),
            forMember(
                e => e.locationId,
                mapFrom(s => s.location?.id)
            ),
            forMember(
                e => e.customerId,
                mapFrom(s => s.customer?.id)
            ),
            forMember(
                e => e.vendorId,
                mapFrom(s => s.vendor?.id)
            )
        );
        createMap<IQueryParams<StopDto>, StopSearchParams>(
            mapper,
            IQueryParamsSymbol,
            symbols.searchParams,
            ...withDefaultSearchParamsMappingConfiguration()
        );

        StopItemMetadataMap.profile(mapper);
        ContactMetadataMap.profile(mapper);
        StopChargeMetadataMap.profile(mapper);
        DefaultLocationFragmentMetadataMap.profile(mapper);
        EventMetadataMap.profile(mapper);
        ReferenceMetadataMap.profile(mapper);
        CustomFieldMetadataMap.profile(mapper);
        StopStatusMetadataMap.profile(mapper);
    },
};
